import { from, Observable, throwError as observableThrowError } from 'rxjs';

import { Injectable } from '@angular/core';
import { IndexIntradayViewModel } from '@models/index-intraday.class';
import { IntradayAggregateViewModel } from '@models/intraday-aggregate.class';
import { IssueDetailFundamentalsViewModel } from '@models/issue-detail-fundamentals.class';
import { LevelViewModel } from '@models/level-two.class';
import { MarketViewModel } from '@models/market.class';
import { Pagination } from '@models/pagination';
import { SelectedPuppetTab } from '@models/puppet.class';
import { SecurityDetailViewModel } from '@models/security-detail.class';
import { SharePercentageViewModel } from '@models/share-percentage.class';
import { TradesViewModel } from '@models/trades.class';
import { TradingMarketConfiguration } from '@models/trading-market-configuration.class';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { isEmpty } from 'lodash';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { HTTP_STATUS_CODE } from 'src/app';
import { SelectTab } from 'src/app/state/actions/operation-puppet.actions';
import { AppState } from 'src/app/state/app-state';
import {
  CURRENT_SYMBOL,
  MARKET_OPERATION_TIME,
} from 'src/app/state/app.reducer';
import { IIssueDetailFundamentalsResponse } from 'src/core/responses/issue-detail-fundamentals.response';
import { NotificationService } from 'src/core/services/notification.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
import { CommodityGroups, MarketType, MarketTypeString } from 'src/solace';

import { AppManagementService } from '@services/appManagement.service';
import { ApiGatewayService } from 'src/shared';

@Injectable()
export class MarketService {
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;
  private gbmComAPI: string;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private store: Store<AppState>,
    private appManagementRestService: AppManagementService,
    private notificationService: NotificationService,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
        take(1),
      )
      .subscribe((value) => {
        this.gbmAPI = value.api;
        this.gbmComAPI = value.gbmComApi;
      });
  }

  getCapitalMarketOperationTime(): Observable<any> {
    return this.appManagementRestService.getCapitalMarketOperationTime().pipe(
      catchError((error) => this.handleError(error)),
      tap((result: any) => {
        this.store.dispatch({
          type: MARKET_OPERATION_TIME,
          payload: result,
        });
      }),
    );
  }

  public getTradingMarketConfiguration(
    isUsa: boolean,
  ): Observable<Partial<TradingMarketConfiguration>> {
    return this.appManagementRestService.getTradingMarketConfiguration().pipe(
      map((tradingMarketConfiguration: TradingMarketConfiguration) => {
        if (isUsa) {
          return {
            isNormalOperationTime:
              tradingMarketConfiguration.isNormalOperationTimeUsa,
            startTime: tradingMarketConfiguration.startTimeUsa,
            endTime: tradingMarketConfiguration.endTimeUsa,
            isWorkingDay: tradingMarketConfiguration.isWorkingDayUsa,
            nextWorkingDate: tradingMarketConfiguration.nextWorkingDateUsa,
          };
        }
        return {
          isNormalOperationTime:
            tradingMarketConfiguration.isNormalOperationTime,
          startTime: tradingMarketConfiguration.startTime,
          endTime: tradingMarketConfiguration.endTime,
          isWorkingDay: tradingMarketConfiguration.isWorkingDay,
          nextWorkingDate: tradingMarketConfiguration.nextWorkingDate,
        };
      }),
      catchError((error) => this.handleError(error)),
    );
  }

  searchIssue(searchTerm: string): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Market/SearchIssue/${searchTerm}`)
      .pipe(catchError((error) => this.handleError(error)));
  }

  searchSpecificIssue(issue: string): void {
    issue = new ChangeCharacterFromIssue().ChangeToAmpersand(issue);
    this.searchIssue(issue)
      .pipe(
        filter((result: any[]) => result.length > 0),
        map((results: any[]) =>
          results.filter((individual: any) => individual.issueID === issue),
        ),
        filter((result: any[]) => result.length > 0),
        map((result: any[]) => result[0]),
      )
      .subscribe((resultIssue: any) => {
        resultIssue.issueID = new ChangeCharacterFromIssue().ChangeAmpersand(
          resultIssue.issueID,
        );
        this.store.dispatch({
          type: CURRENT_SYMBOL,
          payload: resultIssue,
        });
        this.store.dispatch(new SelectTab(SelectedPuppetTab.TRADE));
      });
  }

  getHistoricalAggregates(
    issueId: string,
    market: string = 'BMV',
    startDate: string,
    endDate: string,
    isTimer?: boolean,
  ): Observable<IntradayAggregateViewModel[]> {
    const url = `${this.gbmComAPI}v1/markets/${market}/securities/${issueId}/historical-trades?start_date=${startDate}&end_date=${endDate}`;

    return this.apiGatewayService.get(url, isTimer).pipe(
      map((aggregates: any) =>
        aggregates
          .filter((item) => item !== null)
          .map((item: any) => IntradayAggregateViewModel.fromJSON(item)),
      ),
      catchError(
        (error) =>
          error.code !== HTTP_STATUS_CODE.NOT_FOUND && this.handleError(error),
      ),
    );
  }

  getIntradayAggregates(
    issueId: string,
    market: string = 'BMV',
    seconds: number = 60,
    isTimer?: any,
  ): Observable<IntradayAggregateViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v2/markets/${market}/securities/${issueId}/intraday-trade-aggregates?timespan=${seconds}`,
        isTimer,
      )
      .pipe(
        map((aggregates: any) =>
          !isEmpty(aggregates)
            ? aggregates.map((item: any) =>
                IntradayAggregateViewModel.fromJSON(item),
              )
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getInstrumentPricesIntradayPPP(
    issueId: string,
    isTimer?: any,
  ): Observable<any> {
    const isOnLine = true;

    const observableResponse$: Observable<any> = this.apiGatewayService.post(
      `${this.gbmAPI}api/Market/GetInstrumentPricesIntradayPPP/${issueId}`,
      { IsOnLine: isOnLine },
      isTimer,
    );

    observableResponse$.pipe(
      catchError((error) => this.handleError(error)),
      take(1),
    );

    return observableResponse$;
  }

  getLevelOne(
    issueId: string,
    market: string = 'BMW',
  ): Observable<LevelViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/markets/${market}/securities/${issueId}/level-one`,
      )
      .pipe(
        map((levelOne: any) =>
          !isEmpty(levelOne) ? [LevelViewModel.fromJSON(levelOne)] : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
        take(1),
      );
  }

  getLevelTwo(
    issueId: string,
    market: string = 'BMV',
  ): Observable<LevelViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/markets/${market}/securities/${issueId}/level-two`,
      )
      .pipe(
        map((levelTwo: any) => {
          return !isEmpty(levelTwo)
            ? levelTwo
                .map((item: any) => LevelViewModel.fromJSON(item))
                .sort(
                  (a: LevelViewModel, b: LevelViewModel) =>
                    a.sequence - b.sequence,
                )
            : [];
        }),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
        take(1),
      );
  }

  getCommodity(commodityId: CommodityGroups) {
    return this.apiGatewayService.get(
      `${this.gbmComAPI}v1/markets/commodities?type_id=${commodityId}`,
    );
  }

  getCommodityValuesByRange(
    index: string,
    startDate: any,
    endDate: any,
    isTimer?: boolean,
  ): Observable<any> {
    const data = {
      issueID: index,
      startDate,
      endDate,
    };
    const observableResponse$: Observable<any> = this.apiGatewayService.post(
      `${this.gbmAPI}api/Market/GetCommodityValuesByRange`,
      data,
      isTimer,
    );

    observableResponse$.pipe(
      catchError((error) => this.handleError(error)),
      take(1),
    );

    return observableResponse$;
  }

  getMarketChartConfiguration(): Observable<any> {
    const observableResponse$: Observable<any> =
      this.appManagementRestService.getMarketChartConfiguration();

    observableResponse$.pipe(
      catchError((error) => this.handleError(error)),
      take(1),
    );

    return observableResponse$;
  }

  addMarketChartConfiguration(
    name: string,
    configuration: any,
  ): Observable<any> {
    const observableResponse$: Observable<any> =
      this.appManagementRestService.addMarketChartConfiguration(
        name,
        configuration,
      );

    observableResponse$.pipe(
      catchError((error) => this.handleError(error)),
      take(1),
    );

    return observableResponse$;
  }

  deleteMarketChartConfiguration(chartId: number): Observable<any> {
    const observableResponse$: Observable<any> =
      this.appManagementRestService.deleteMarketChartConfiguration(chartId);

    observableResponse$.pipe(catchError((error) => this.handleError(error)));

    return observableResponse$;
  }

  updateMarketChartConfiguration(data: any): Observable<any> {
    const observableResponse$: Observable<any> =
      this.appManagementRestService.updateMarketChartConfiguration(data);

    observableResponse$.pipe(catchError((error) => this.handleError(error)));

    return observableResponse$;
  }

  getAvailableMarkets(): Observable<any> {
    const dummyResult = [
      {
        id: 1,
        Name: 'S_BVM',
        Description: 'Bolsa Mexicana de Valores',
      },
      {
        id: 2,
        Name: 'GBM_IPC',
        Description: 'IPC',
      },
      {
        id: 3,
        Name: '8_BMV',
        Description: 'ETFs locales (BMV)',
      },
      {
        id: 4,
        Name: 'S_SIC',
        Description: 'Acciones internacionales (SIC)',
      },
      {
        id: 5,
        Name: '8_SIC',
        Description: 'ETFs internacionales (SIC)',
      },
      {
        id: 6,
        Name: 'I_GBM',
        Description: 'Índices Mundiales',
      },
    ];

    const observableResult$ = from(dummyResult);

    observableResult$.pipe(catchError((error) => this.handleError(error)));

    return observableResult$;
  }

  getAvaliableShortSellEquities(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Market/GetAvailableShortSellEquities`)
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Return market details of the issueID
   * @param issueID  - The related issueID
   */
  getIssueMarketDetail(issueID: string): Observable<Response> {
    return this.apiGatewayService.get(
      `${this.gbmAPI}api/Market/GetIssueMarketDetail/${issueID}`,
    );
  }

  getCompanySharePercentage(data: any, issueID: string): Observable<any> {
    return this.apiGatewayService
      .post(
        `${this.gbmAPI}api/Market/GetCompanySharePercentage/${issueID}`,
        data,
      )
      .pipe(
        map((sharePercentage: any) =>
          !isEmpty(sharePercentage)
            ? sharePercentage.map((item: any) =>
                SharePercentageViewModel.fromDigitalJSON(item),
              )
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getMDMarketData(issueId: string): Observable<TradesViewModel[]> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Market/GetMDMarketData/${issueId}`)
      .pipe(
        map((trades: any) =>
          !isEmpty(trades)
            ? trades.map((item: any) => TradesViewModel.fromDigitalJSON(item))
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getIndexIntraday(
    index: string,
    isOnLine: boolean,
  ): Observable<IndexIntradayViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v2/markets/indexs/securities/${index}/intraday-trades`,
        isOnLine,
      )
      .pipe(
        map((intraday: any) =>
          !isEmpty(intraday)
            ? intraday.map((item: any) => IndexIntradayViewModel.fromJSON(item))
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getSharePercentage(
    issueId: string,
    market: string,
  ): Observable<SharePercentageViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/markets/${market}/securities/${issueId}/share-percentages`,
      )
      .pipe(
        map((sharePercentage: any) =>
          !isEmpty(sharePercentage)
            ? sharePercentage.map((item: any) =>
                SharePercentageViewModel.fromJSON(item),
              )
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getSecurityDetail(
    issueId: string,
    market: string = 'BMW',
  ): Observable<SecurityDetailViewModel[]> {
    return this.apiGatewayService
      .get(`${this.gbmComAPI}v1/markets/${market}/securities/${issueId}/detail`)
      .pipe(
        map((securityDetail: any) =>
          !isEmpty(securityDetail)
            ? [SecurityDetailViewModel.fromJSON(securityDetail)]
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getTradeDetails(
    marketName: string,
    issueId: string,
    page: number,
    pageSize: number,
  ): Observable<Pagination<TradesViewModel>> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v2/markets/${marketName}/securities/${issueId}/trade-details?page=${page}&page_size=${pageSize}`,
      )
      .pipe(
        map((trades: any) => {
          const itemsTransformed = !isEmpty(trades.items)
            ? trades.items.map((item: any) => TradesViewModel.fromJSON(item))
            : [];
          trades.items = itemsTransformed;
          return trades;
        }),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getMarket(marketName: MarketTypeString): Observable<MarketViewModel[]> {
    return this.apiGatewayService
      .get(`${this.gbmComAPI}v1/markets/${marketName}`)
      .pipe(
        map((market: any) =>
          !isEmpty(market)
            ? market.map((item: any) => {
                const marketView = MarketViewModel.fromJSON(item);
                return marketName === MarketTypeString.USA
                  ? {
                      ...marketView,
                      instrumentType: MarketType.USA,
                      variationPercentage: marketView.variationPercentage * 100,
                    }
                  : marketView;
              })
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getMarketWatchlist(
    marketName: string,
    watchlist: string,
  ): Observable<MarketViewModel[]> {
    return this.apiGatewayService
      .get(
        `${this.gbmComAPI}v1/markets/${marketName}?security_ids=${watchlist}`,
      )
      .pipe(
        map((market: any) =>
          !isEmpty(market)
            ? market.map((item: any) => MarketViewModel.fromJSON(item))
            : [],
        ),
        catchError(
          (error) =>
            error.code !== HTTP_STATUS_CODE.NOT_FOUND &&
            this.handleError(error),
        ),
      );
  }

  getSecurityFundamentals(
    marketName: string,
    issueId: string,
  ): Observable<IssueDetailFundamentalsViewModel> {
    return this.apiGatewayService
      .get<IIssueDetailFundamentalsResponse>(
        `${this.gbmComAPI}v1/markets/${marketName}/securities/${issueId}/fundamentals`,
      )
      .pipe(
        map((market) =>
          !isEmpty(market)
            ? IssueDetailFundamentalsViewModel.fromJSON(market)
            : IssueDetailFundamentalsViewModel.empty(),
        ),
        catchError((error) => this.handleError(error)),
      );
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);

    return observableThrowError(error);
  }
}
